import React, { useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const REASON_CATEGORIES: Record<string, string[]> = {
    'Cannot register to a tournament': [
        'Payment Problems',
        'Package/squad selection problems',
        'Division Problems',
        'Checkout questions',
    ],
    'Tournament I registered for not showing up': [
        'My tournament is not showing',
        'My squads are wrong',
        "I can't buy re-entries or side events",
        'Group code issues',
        "I can't move my squads",
    ],
    'Issues viewing results': [
        "I can't find the results of a tournament",
        "I can't find the results of a squad",
        'General question on how to interact with the results page',
    ],
    'Issues with my tournament balances': [
        'My balance is wrong',
        "I can't complete a payment",
        'I believe I was double charged or a payment didn\'t process',
    ],
    'Issue with the platform fee': [
        'I paid the platform fee and still can\'t register',
        'I was double charged',
        'Payment keeps failing',
        "Fee payment page won't load/crash",
    ],
    'I need to edit my personal info': [
        'Name / Last Name',
        'Phone',
        'Email',
        'Date of Birth',
        'SSN',
        'USBC #',
        'Address',
    ],
};

const REASONS = Object.keys(REASON_CATEGORIES);

interface SupportFormProps {
    player: any;
}

const SupportForm: React.FC<SupportFormProps> = ({ player }) => {
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState('');
    const [category, setCategory] = useState('');
    const [questions, setQuestions] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const categories = reason ? REASON_CATEGORIES[reason] : [];

    const handleReasonChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setReason(e.target.value);
        setCategory('');
    };

    const handleClose = () => {
        setOpen(false);
        setReason('');
        setCategory('');
        setQuestions('');
        setError('');
        setSuccess(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const res = await fetch(`${BACKEND_URL}/api/support/ticket`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    bowlerClave: player?.CLAVE,
                    bowlerEmail: player?.EMAIL || '',
                    reason,
                    category,
                    subcategory: '',
                    questions,
                }),
            });

            const data = await res.json();
            if (res.ok && data.success) {
                setSuccess(true);
            } else {
                setError(data.error || 'Failed to submit ticket. Please try again.');
            }
        } catch {
            setError('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const canSubmit = reason && category && questions.trim().length > 0;

    return (
        <>
            {/* Floating Button */}
            <button
                onClick={() => setOpen(true)}
                className="fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-full shadow-2xl font-semibold transition-all transform hover:scale-105 hover:shadow-blue-300"
                aria-label="Open support form"
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M18.364 5.636a9 9 0 010 12.728M15.536 8.464a5 5 0 010 7.072M6.343 17.657a9 9 0 010-12.728M9.172 14.828a5 5 0 010-7.072M12 12h.01" />
                </svg>
                Support
            </button>

            {/* Modal Backdrop */}
            {open && (
                <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:justify-end p-0 sm:p-6">
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />

                    {/* Modal Panel */}
                    <div className="relative bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg flex flex-col"
                        style={{ maxHeight: '90vh' }}>

                        {/* Header */}
                        <div className="flex-shrink-0 bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5 rounded-t-3xl sm:rounded-t-2xl">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="bg-white/20 rounded-xl p-2">
                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-bold text-white">Contact Support</h2>
                                        <p className="text-blue-100 text-xs">We'll get back to you as soon as possible</p>
                                    </div>
                                </div>
                                <button onClick={handleClose} className="text-white/80 hover:text-white hover:bg-white/20 rounded-lg p-1.5 transition">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Body */}
                        <div className="flex-1 overflow-y-auto px-6 py-5">
                            {success ? (
                                <div className="py-10 text-center">
                                    <div className="flex justify-center mb-4">
                                        <div className="bg-green-100 rounded-full p-4">
                                            <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">Ticket Submitted!</h3>
                                    <p className="text-gray-600 text-sm mb-6">Our team has been notified and will reach out to you at <strong>{player?.EMAIL}</strong>.</p>
                                    <button
                                        onClick={handleClose}
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition"
                                    >
                                        Close
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    {/* Bowler info read-only */}
                                    <div className="bg-blue-50 rounded-xl px-4 py-3 text-sm text-blue-800">
                                        <span className="font-semibold">{player?.NOMBRECOMPLETO}</span>
                                        <span className="mx-2 text-blue-300">·</span>
                                        <span>TAT: {player?.FICHANAL}</span>
                                        <span className="mx-2 text-blue-300">·</span>
                                        <span>{player?.EMAIL}</span>
                                    </div>

                                    {/* Reason */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                                            What are you having trouble with? <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            value={reason}
                                            onChange={handleReasonChange}
                                            required
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                                        >
                                            <option value="">Select a reason...</option>
                                            {REASONS.map(r => (
                                                <option key={r} value={r}>{r}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Category */}
                                    {reason && (
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-1">
                                                More specifically... <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={category}
                                                onChange={e => setCategory(e.target.value)}
                                                required
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                                            >
                                                <option value="">Select a category...</option>
                                                {categories.map(c => (
                                                    <option key={c} value={c}>{c}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {/* Description */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                                            Please describe your issue <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            value={questions}
                                            onChange={e => setQuestions(e.target.value)}
                                            required
                                            rows={4}
                                            placeholder={
                                                reason === 'I need to edit my personal info'
                                                    ? 'Please tell us what the correct value should be (e.g. "My last name should be Rodriguez, not Rodrigez").'
                                                    : 'Tell us what happened, what you expected, and any steps you already tried...'
                                            }
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                        />
                                        <p className="text-xs text-gray-400 mt-1 text-right">{questions.length} characters</p>
                                    </div>

                                    {error && (
                                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                                            {error}
                                        </div>
                                    )}

                                    <div className="flex gap-3 pt-1">
                                        <button
                                            type="button"
                                            onClick={handleClose}
                                            className="flex-1 border border-gray-300 text-gray-700 px-4 py-2.5 rounded-lg font-semibold hover:bg-gray-50 transition text-sm"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={!canSubmit || loading}
                                            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2.5 rounded-lg font-semibold transition text-sm"
                                        >
                                            {loading ? 'Submitting...' : 'Submit Ticket'}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SupportForm;
